import { Slot } from '@radix-ui/react-slot';
import { cva, VariantProps } from 'class-variance-authority';
import { LucideProps } from 'lucide-react';
import React from 'react';

import { cn } from '@/utils/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl border text-base font-semibold ring-ring transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&>svg]:size-5',
  {
    variants: {
      variant: {
        primary: 'bg-accent-brand text-white hover:bg-accent-brand-hover',
        destructive: 'border-red-600 bg-red-600 text-white hover:bg-red-700',
        success:
          'hover:bg-teal/90 border-emerald-200 bg-emerald-200 text-emerald-800 disabled:text-teal-800/50',
        ghost:
          'border-primary-200 hover:border-primary-300 bg-white text-primary hover:bg-white/0 disabled:text-primary-foreground/50',
        outline: 'button-outline hover:bg-muted',
        light:
          'border-transparent bg-transparent text-zinc-600 hover:bg-primary/[.07] hover:text-zinc-800',
        icon: 'border-0 bg-transparent hover:bg-muted',
        dark: 'border-zinc-800 bg-zinc-800 text-white hover:bg-zinc-700'
      },
      size: {
        default: 'h-10 rounded-lg px-6 py-5',
        xs: 'h-7 rounded-md px-2.5 text-xs',
        sm: 'h-9 rounded-md px-3 text-sm',
        lg: 'h-12 rounded-lg px-8',
        xl: 'h-14 rounded-xl px-10 text-lg',
        '2xl': 'h-16 rounded-full px-16 text-lg',
        icon: 'size-12',
        iconSm: 'size-9'
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default'
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  fullWidthMobile?: boolean;
  asChild?: boolean;
  icon?: React.ComponentType<LucideProps>;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      size,
      fullWidthMobile,
      className,
      asChild = false,
      icon: Icon,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(
          buttonVariants({ variant, size }),
          fullWidthMobile ? 'w-full md:w-auto' : '',
          className
        )}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {Icon && <Icon />}
            {children}
          </>
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button };
